/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';
import { useLang } from 'hooks/lang';

import { Tab, TabsList } from './Tabs';
import { TabContentProps, TabsContent } from './Content';
import { TabProvider } from './Provider';
import { TabProviderProps, useTab } from './context';

import { overlayEscape, OverlayOption, OverlaySpecificProps, ResponsiveOverlay } from '../Overlays';
import { ChevronDownIcon } from '../Icons';
import { ComponentOrNode } from '../helpers';

import { useIsBreakpoint } from 'hooks';

import { getFirst, listGet } from 'utils/array';
import { cn, ResponsiveBreakpoint } from 'styles';
import { GetLabelFn } from 'types';

type OverlayProps =
  | {
      /**
       * Which overlay will be rendered at the `responsiveBreakpoint`
       *
       * Defaults to `pop-over`
       */
      responsiveOverlay?: Extract<OverlayOption, 'pop-over'>;
      overlayProps?: OverlaySpecificProps<'pop-over'>;
    }
  | {
      /**
       * Which overlay will be rendered at the `responsiveBreakpoint`
       *
       * Defaults to `pop-over`
       */
      responsiveOverlay?: Extract<OverlayOption, 'drawer'>;

      overlayProps?: OverlaySpecificProps<'drawer'>;
    };

export type SingletonTabsProps = Omit<TabProviderProps, 'children'> &
  OverlayProps & {
    tabs: Array<{
      value: string;
      label?: ReactNode;
      getLabel?: GetLabelFn;
      disabled?: boolean;
      icon?: ReactNode | ((props?: any) => JSX.Element);
      content?: TabContentProps['children'];
      hide?: boolean;
    }>;

    tabListClassName?: string;
    tabClassName?: string;
    contentClassName?: string;

    disabled?: boolean;

    /**
     * The width breakpoint to which the tab layout will shift to
     * render a single tab (the one selected) + render a drawer once clicked
     * to display the options
     */
    responsiveBreakpoint?: ResponsiveBreakpoint;

    /**
     * If it should auto-close the `responsiveOverlay` after a selection is made
     *
     * Defaults to `true`
     */
    escapeOverlayOnSelect?: boolean;

    /**
     * Disabled the default keyboard navigation (arrow keys etc)
     *
     * Useful if you are controlling the same keys for a specific part of the app
     *
     * Normally this should not be disabled
     */
    blockKeyboardNav?: boolean;
  };

type ContentProps = {
  tabs: SingletonTabsProps['tabs'];
  className?: string;
};

function Content({ tabs, className }: ContentProps) {
  const { value: selectedTab } = useTab();

  const { content } = listGet(tabs, selectedTab, 'value') ?? getFirst(tabs) ?? {};

  if (!content) return null;

  return (
    <TabsContent className={cn('pl-2 pt-2', className)} value={selectedTab}>
      {content}
    </TabsContent>
  );
}

export function InnerTabs({
  tabs,
  tabClassName,
  tabListClassName,
  contentClassName,
  responsiveBreakpoint,
  responsiveOverlay = 'pop-over',
  disabled,
  overlayProps,
  blockKeyboardNav,
  escapeOverlayOnSelect = true,
  ...providerProps
}: SingletonTabsProps) {
  const { t } = useLang();

  const { value } = useTab();

  const selectedTab = listGet(tabs, value, 'value') ?? getFirst(tabs);

  const breakpointActive = useIsBreakpoint(responsiveBreakpoint || 'SMALL_PHONE');

  const isMobile = responsiveBreakpoint ? breakpointActive : false;

  return (
    <>
      {isMobile ? (
        <ResponsiveOverlay
          {...overlayProps}
          className={responsiveOverlay === 'pop-over' ? 'w-auto p-0' : ''}
          switchTo={responsiveOverlay}
          trigger={
            <Tab
              disabled={disabled || selectedTab.disabled}
              value={providerProps.value!}
              className={tabClassName}
              modalTrigger
            >
              <ComponentOrNode target={selectedTab.icon} />

              {selectedTab.getLabel?.(t) ?? selectedTab.label ?? '_PASS-LABEL_'}

              {!disabled && <ChevronDownIcon size={10} />}
            </Tab>
          }
        >
          <TabsList className={cn('h-auto flex-col items-start p-1', tabListClassName)}>
            {tabs
              .filter((x) => !x.hide)
              .map(({ label, value, icon, getLabel, ...tab }) => (
                <Tab
                  key={value}
                  value={value}
                  disabled={disabled || tab.disabled}
                  className={cn('w-full justify-start', tabClassName)}
                  onClick={escapeOverlayOnSelect ? overlayEscape : undefined}
                >
                  <ComponentOrNode target={icon} />

                  {getLabel?.(t) ?? label ?? '_PASS-LABEL_'}
                </Tab>
              ))}
          </TabsList>
        </ResponsiveOverlay>
      ) : (
        <TabsList className={tabListClassName}>
          {tabs
            .filter((x) => !x.hide)
            .map(({ label, value, icon, getLabel, disabled }) => (
              <Tab
                key={value}
                value={value}
                disabled={disabled}
                blockKeyboardNav={blockKeyboardNav}
                className={tabClassName}
              >
                <ComponentOrNode target={icon} />

                {getLabel?.(t) ?? label ?? '_PASS-LABEL_'}
              </Tab>
            ))}
        </TabsList>
      )}

      <Content tabs={tabs} className={contentClassName} />
    </>
  );
}

export function Tabs(props: SingletonTabsProps) {
  return (
    <TabProvider
      {...props}
      defaultValue={props.value ?? props.defaultValue ?? getFirst(props.tabs)?.value}
    >
      <InnerTabs {...props} />
    </TabProvider>
  );
}
